import React, { createContext, useContext, useState, ReactNode } from 'react';

interface AppState {
  // Sidebar state
  isSidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Expanded sections in sidebar
  expandedSections: Record<string, boolean>;
  toggleSection: (id: string) => void;
  setExpandedSection: (id: string, expanded: boolean) => void;

  // User preferences (for future)
  theme: 'light' | 'dark';
  language: 'ja' | 'en';
}

const AppContext = createContext<AppState | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    teap: true,
    'teap-speaking': true,
    'teap-writing': false,
  });
  const [theme] = useState<'light' | 'dark'>('light');
  const [language] = useState<'ja' | 'en'>('ja');

  const toggleSection = (id: string) => {
    if (isSidebarCollapsed) {
      setSidebarCollapsed(false);
      setTimeout(() => {
        setExpandedSections(prev => ({ ...prev, [id]: true }));
      }, 150);
      return;
    }
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const setExpandedSection = (id: string, expanded: boolean) => {
    setExpandedSections(prev => ({ ...prev, [id]: expanded }));
  };

  return (
    <AppContext.Provider value={{
      isSidebarCollapsed,
      setSidebarCollapsed,
      expandedSections,
      toggleSection,
      setExpandedSection,
      theme,
      language,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}
